<script setup lang="ts">
import { computed } from "vue"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel
} from "@/components/ui/dropdown-menu"
import * as Icons from "lucide-vue-next"
import bus from "@/lib/bus"

/* ===== Tipos ===== */
type BaseAction = {
  id?: string
  label?: string
  icon?: string               // nome do ícone lucide
  on?: string                 // chave do event-bus
  title?: string              // tooltip nativo
  disabled?: boolean
  loading?: boolean
  destructive?: boolean
  shortcut?: string           // "Ctrl+K" etc. (visual apenas aqui)
}

type ActionButton = BaseAction & {
  type: "button"
  variant?: "default" | "outline" | "secondary" | "ghost" | "link" | "destructive" | "primary"
  size?: "sm" | "default" | "lg" | "icon"
}

type ActionMenuItem = BaseAction & {
  separator?: false
}

type ActionMenuSeparator = { separator: true }

type ActionMenu = BaseAction & {
  type: "menu"
  items: (ActionMenuItem | ActionMenuSeparator)[]
}

type Action = ActionButton | ActionMenu

const props = defineProps<{ items: Action[] | undefined }>()

/* ===== Icon resolver com cache ===== */
const iconCache = new Map<string, any>()
const getIcon = (name?: string) => {
  if (!name) return null
  if (iconCache.has(name)) return iconCache.get(name)
  const mod = (Icons as any)[name]
  iconCache.set(name, mod ?? null)
  return mod ?? null
}

/* ===== Emit (event-bus) ===== */
const emitAction = (event?: string) => {
  if (!event) return
  bus.emit(event)
}

/* ===== Classe para variantes destrutivas/disabled ===== */
const itemClass = (a: BaseAction) =>
    `${a.destructive ? 'text-destructive' : ''} ${a.disabled ? 'opacity-60 pointer-events-none' : ''}`

/* ===== Normalização de itens (sempre array) ===== */
const actions = computed(() => Array.isArray(props.items) ? props.items : [])
</script>

<template>
  <div class="flex items-center gap-2">
    <template v-for="a in actions" :key="a.id || a.label || JSON.stringify(a)">
      <!-- Botão -->
      <Button
          v-if="a.type === 'button'"
          :size="a.size || 'sm'"
          :variant="a.variant === 'primary' ? 'default' : (a.variant || 'outline')"
          :aria-label="a.label"
          :aria-disabled="a.disabled || a.loading"
          :disabled="a.disabled || a.loading"
          :title="a.title || a.label"
          @click="emitAction(a.on)"
          class="inline-flex items-center"
      >
        <component :is="getIcon(a.icon)" v-if="a.icon" class="h-4 w-4" />
        <span>{{ a.label }}</span>
        <span v-if="a.shortcut" class="ml-2 text-xs text-muted-foreground">{{ a.shortcut }}</span>
      </Button>

      <!-- Menu -->
      <DropdownMenu v-else-if="a.type === 'menu'">
        <DropdownMenuTrigger as-child>
          <Button
              variant="outline"
              size="icon"
              class="h-9 w-9"
              :aria-label="a.title || a.label || 'Mais ações'"
              :title="a.title || a.label || 'Mais ações'"
              :aria-disabled="a.disabled"
              :disabled="a.disabled"
          >
            <component :is="getIcon(a.icon) || getIcon('MoreHorizontal')" class="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" class="w-48">
          <DropdownMenuLabel>{{ a.label || 'Ações' }}</DropdownMenuLabel>
          <template v-for="mi in (a.items || [])" :key="mi.separator ? Math.random() : (mi.id || mi.label)">
            <DropdownMenuSeparator v-if="(mi as any).separator" role="separator" />
            <DropdownMenuItem
                v-else
                :class="itemClass(mi as any)"
                :aria-disabled="(mi as any).disabled"
                :disabled="(mi as any).disabled"
                @click="emitAction((mi as any).on)"
            >
              <component :is="getIcon((mi as any).icon)" v-if="(mi as any).icon" class="mr-2 h-4 w-4" />
              <span class="flex-1">{{ (mi as any).label }}</span>
              <span v-if="(mi as any).shortcut" class="text-xs text-muted-foreground">
                {{ (mi as any).shortcut }}
              </span>
            </DropdownMenuItem>
          </template>
        </DropdownMenuContent>
      </DropdownMenu>
    </template>
  </div>
</template>
